import { defineStore } from 'pinia';
import ServiceArea from '@/definitions/interfaces/ServiceArea.i';
import FullService from '@/definitions/interfaces/FullService.i';
import { ServiceSubmission } from '@/definitions/interfaces/ServiceSubmission.i';
import ServiceInformation from '@/definitions/interfaces/ServiceInformation.i';
import DataTablePaginationOptions from '@/definitions/interfaces/DataTableSortOptions.i';
import User from '@/definitions/interfaces/User.i';
import Debug from '@/utils/Debug';
import OperatorServiceCache from '@/definitions/interfaces/OperatorServiceCache.i';
import { Filters } from '@/views/operators/logisticsHub/types';
import { ServerOptions } from 'vue3-easy-data-table';

export interface SubstituteSearchFilters {
  applicationsFromDate: string | null;
  applicationsToDate: string | null;
  applicationsFromText: string | null;
  applicationsToText: string | null;
  assignmentsFromDate: string | null;
  assignmentsToDate: string | null;
  assignmentsFromText: string | null;
  assignmentsToText: string | null;
}

export interface PartnerServicesFilters {
  servicesFromDate: string | null;
  servicesToDate: string | null;
  servicesFromText: string | null;
  servicesToText: string | null;
}

export interface OperatorDataState {
  logisticsHubViewCache: {
    filters: Filters | null;
    cache: boolean;
    serverOptions: ServerOptions | null;
  };
  servicesViewCache: OperatorServiceCache;
  servicesCache: FullService[];
  servicesPaginationCache: DataTablePaginationOptions | null;
  servicesFilterCache: object | null;
  allInclusiveServicesSortCache: DataTablePaginationOptions | null;
  tieredServicesSortCache: DataTablePaginationOptions | null;
  tieredServicesFiltersCache: {
    fromDate: string | null;
    toDate: string | null;
    fromDateISO: string | null;
    toDateISO: string | null;
    tier: string | null;
  } | null;
  tieredServicesPaginationCache: DataTablePaginationOptions | null;
  serviceSubmissionsSortCache: DataTablePaginationOptions | null;
  servicesTieredCache: FullService[];
  servicesAllInclusiveCache: FullService[];
  serviceSubmissionsCache: ServiceSubmission[];
  serviceAreasCache: ServiceArea[];
  serviceAreasSortCache: DataTablePaginationOptions | null;
  usersCache: User[];
  usersPaginationCache: DataTablePaginationOptions | null;
  usersFiltersCache: { searchTerm: string; role: string | null; activeContract: boolean | null } | null;
  fullUsersCache: User[];
  fullServicesCache: FullService[];
  fullServiceInformationCache: { [p: string]: ServiceInformation }[];
  insurancesCache: { [p: string]: any }[];
  specializationsCache: string[];
  accountingServicesCache: FullService[];
  accountingServicesStartCache: FullService[];
  accountingServicesPriorityCache: FullService[];
  accountingServicesFiltersCache: { fromDate: string | null; toDate: string | null; fromDateISO: string | null; toDateISO: string | null } | null;
  accountingServicesPaginationCache: DataTablePaginationOptions | null;
  accountingServicesStartFiltersCache: { fromDate: string | null; toDate: string | null; fromDateISO: string | null; toDateISO: string | null } | null;
  accountingServicesStartPaginationCache: DataTablePaginationOptions | null;
  accountingServicesPriorityFiltersCache: { fromDate: string | null; toDate: string | null; fromDateISO: string | null; toDateISO: string | null } | null;
  accountingServicesPriorityPaginationCache: DataTablePaginationOptions | null;
  reportingServicesCache: FullService[];
  reportingServicesSortCache: DataTablePaginationOptions | null;
  reportingServicesFiltersCache: { fromDate: string | null; toDate: string | null; fromDateISO: string | null; toDateISO: string | null } | null;
  reportingServicesPaginationCache: DataTablePaginationOptions | null;
  reportingUsersCache: User[];
  reportingUsersSortCache: DataTablePaginationOptions | null;
  reportingUsersFiltersCache: {
    fromDate: string | null;
    toDate: string | null;
    fromDateISO: string | null;
    toDateISO: string | null;
    role: string | null;
  } | null;
  reportingUsersPaginationCache: DataTablePaginationOptions | null;
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  substituteSearchFiltersCache: { [userId: string]: SubstituteSearchFilters };
  partnerServicesFiltersCache: { [userId: string]: PartnerServicesFilters };
}

const useOperatorDataStore = defineStore('OperatorData', {
  /**
    Define defaults
  */
  state: (): OperatorDataState => ({
    logisticsHubViewCache: {
      filters: null,
      cache: true,
      serverOptions: null,
    },
    servicesViewCache: {
      operatorServicesView: '',
      start: '',
      calendarView: '',
    },
    servicesCache: [],
    servicesPaginationCache: null,
    servicesFilterCache: null,
    allInclusiveServicesSortCache: null,
    tieredServicesSortCache: null,
    tieredServicesFiltersCache: null,
    tieredServicesPaginationCache: null,
    serviceSubmissionsSortCache: null,
    servicesTieredCache: [],
    servicesAllInclusiveCache: [],
    serviceSubmissionsCache: [],
    serviceAreasCache: [],
    serviceAreasSortCache: null,
    usersCache: [],
    usersPaginationCache: null,
    usersFiltersCache: null,
    fullUsersCache: [],
    fullServicesCache: [],
    fullServiceInformationCache: [],
    insurancesCache: [],
    specializationsCache: [],
    accountingServicesCache: [],
    accountingServicesStartCache: [],
    accountingServicesPriorityCache: [],
    accountingServicesFiltersCache: null,
    accountingServicesPaginationCache: null,
    accountingServicesStartFiltersCache: null,
    accountingServicesStartPaginationCache: null,
    accountingServicesPriorityFiltersCache: null,
    accountingServicesPriorityPaginationCache: null,
    reportingServicesCache: [],
    reportingServicesSortCache: null,
    reportingServicesFiltersCache: null,
    reportingServicesPaginationCache: null,
    reportingUsersCache: [],
    reportingUsersSortCache: null,
    reportingUsersFiltersCache: null,
    reportingUsersPaginationCache: null,
    isImpersonating: false,
    impersonatedUserId: null,
    substituteSearchFiltersCache: {},
    partnerServicesFiltersCache: {},
  }),
  actions: {
    startImpersonation(userId: string) {
      this.isImpersonating = true;
      this.impersonatedUserId = userId;
    },
    stopImpersonation() {
      this.isImpersonating = false;
      this.impersonatedUserId = null;
    },
    /**
     * reset partner store
     * @return {void}
     */
    resetServicesViewCache(): void {
      this.servicesViewCache = {
        operatorServicesView: '',
        start: '',
        calendarView: '',
      };
    },
    /**
     * reset partner store
     * @return {void}
     */
    resetOperatorStore(): void {
      this.servicesViewCache = {
        operatorServicesView: '',
        start: '',
        calendarView: '',
      };
      this.servicesCache = [];
      this.servicesPaginationCache = null;
      this.allInclusiveServicesSortCache = null;
      this.tieredServicesSortCache = null;
      this.tieredServicesFiltersCache = null;
      this.tieredServicesPaginationCache = null;
      this.serviceSubmissionsSortCache = null;
      this.servicesTieredCache = [];
      this.servicesAllInclusiveCache = [];
      this.serviceSubmissionsCache = [];
      this.serviceAreasCache = [];
      this.serviceAreasSortCache = null;
      this.usersCache = [];
      this.usersPaginationCache = null;
      this.fullUsersCache = [];
      this.fullServicesCache = [];
      this.fullServiceInformationCache = [];
      this.insurancesCache = [];
      this.specializationsCache = [];
      this.accountingServicesCache = [];
      this.accountingServicesStartCache = [];
      this.accountingServicesPriorityCache = [];
      this.accountingServicesFiltersCache = null;
      this.accountingServicesPaginationCache = null;
      this.accountingServicesStartFiltersCache = null;
      this.accountingServicesStartPaginationCache = null;
      this.accountingServicesPriorityFiltersCache = null;
      this.accountingServicesPriorityPaginationCache = null;
      this.reportingServicesCache = [];
      this.reportingServicesSortCache = null;
      this.reportingServicesFiltersCache = null;
      this.reportingServicesPaginationCache = null;
      this.reportingUsersCache = [];
      this.reportingUsersSortCache = null;
      this.reportingUsersFiltersCache = null;
      this.reportingUsersPaginationCache = null;
      this.isImpersonating = false;
      this.impersonatedUserId = null;
      this.substituteSearchFiltersCache = {};
      this.partnerServicesFiltersCache = {};
    },
  },
  persist: {
    key: 'arztpool24.de-operators',
    debug: Debug.isEnabled(),
  },
});

export default useOperatorDataStore;
